import type { ComponentProps } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import ErrorBoundary from "@/components/ErrorBoundary";
import CareerProfileFocusedChrome from "../_CareerProfileFocusedChrome";
import ExperienceIndexAnswersSkeleton from "../_ExperienceIndexAnswersSkeleton";
import ExperienceIndexResultsSkeleton from "../_ExperienceIndexResultsSkeleton";
import { useCareerProfileQuery } from "../_useCareerProfileQuery";

type FocusedBackLink = NonNullable<ComponentProps<typeof CareerProfileFocusedChrome>["backLink"]>;

export default function ExperienceIndexLayout() {
  const location = useLocation();
  const query = useCareerProfileQuery();
  const isAssessment = /\/assessment\/?$/.test(location.pathname);

  if (!query.flagsLoaded || !query.capabilitiesLoaded) return null;
  if (query.employmentRedirect) return <Navigate to={query.employmentRedirect} replace />;
  if (!query.pgaId) return null;

  return (
    <CareerProfileFocusedChrome
      pgaId={query.pgaId}
      activeTab="experience-index"
      backLink={
        isAssessment
          ? ({
              to: "/people/:pgaId/career/career-profile/experience-assessment",
              params: { pgaId: query.pgaId },
              title: "Back"
            } as unknown as FocusedBackLink)
          : undefined
      }
    >
      {query.loading ? (
        isAssessment ? (
          <ExperienceIndexAnswersSkeleton />
        ) : (
          <ExperienceIndexResultsSkeleton />
        )
      ) : query.error ? (
        <ErrorBoundary
          variant="inline"
          className="mt-12"
          title="Unable to Load Experience Index"
          message={query.error.message}
        />
      ) : (
        <Outlet context={query} />
      )}
    </CareerProfileFocusedChrome>
  );
}
